/**
 * Triples Extractor - Extracts semantic triples from CSV row
 * Supports 35 common fields for SHOPLINE and Shopify conversions
 */

import { cleanValue } from './csvParser.js';
import { getFieldName } from './fieldMapper.js';

/**
 * Extract semantic triples from CSV row
 * @param {Object} row - CSV row data
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {Array} - Triples array
 */
export function extractTriples(row, platform = 'shopline') {
  const triples = [];

  // Helper to get field value
  const getField = (field) => {
    const columnName = getFieldName(platform, field);
    return columnName && row[columnName] ? cleanValue(row[columnName]) : null;
  };

  // Helper to add triple
  const addTriple = (subject, predicate, object) => {
    if (object && String(object).trim()) {
      triples.push([subject, predicate, String(object).trim()]);
    }
  };

  // ========== Core Fields ==========
  const vendorField = getFieldName(platform, 'brand');
  const tagsField = getFieldName(platform, 'tags');
  const collectionsField = getFieldName(platform, 'collections');
  const titleField = getFieldName(platform, 'title');

  // Add brand triple
  if (vendorField && row[vendorField]) {
    addTriple('brand', '=', cleanValue(row[vendorField]));
  }

  // Add tag triples
  if (tagsField && row[tagsField]) {
    cleanValue(row[tagsField])
      .split(',')
      .map(item => item.trim())
      .filter(item => item)
      .forEach(item => addTriple('tag', '=', item));
  }

  // Add category/collections triples
  if (collectionsField && row[collectionsField]) {
    cleanValue(row[collectionsField])
      .split(',')
      .map(item => item.trim())
      .filter(item => item)
      .forEach(item => addTriple('category', '=', item));
  }

  // Add title triple
  if (titleField && row[titleField]) {
    addTriple('title', '=', cleanValue(row[titleField]));
  }

  // ========== Variant Options (3 pairs) ==========
  for (let i = 1; i <= 3; i++) {
    const optNameField = getFieldName(platform, `option${i}Name`);
    const optValueField = getFieldName(platform, `option${i}Value`);

    if (optNameField && optValueField && row[optNameField] && row[optValueField]) {
      const optName = cleanValue(row[optNameField]).toLowerCase();
      const optValue = cleanValue(row[optValueField]);
      addTriple(`option_${optName}`, '=', optValue);
    }
  }

  // ========== SEO Fields ==========
  const seoTitle = getField('seoTitle');
  const seoDesc = getField('seoDescription');
  const seoKeywords = getField('seoKeywords');

  if (seoTitle) {addTriple('seo_title', '=', seoTitle);}
  if (seoDesc) {addTriple('seo_description', '=', seoDesc);}
  if (seoKeywords) {
    seoKeywords.split(',').map(k => k.trim()).filter(k => k).forEach(keyword => {
      addTriple('seo_keyword', '=', keyword);
    });
  }

  // ========== Status & Visibility ==========
  const status = getField('status');
  const published = getField('published');

  if (status) {addTriple('status', '=', status.toLowerCase());}
  if (published !== null) {
    addTriple('published', '=', published.toLowerCase() === 'true' || published === 'Y' ? 'true' : 'false');
  }

  // ========== Inventory & Logistics ==========
  const weight = getField('weight');
  const weightUnit = getField('weightUnit');
  const barcode = getField('barcode');
  const hsCode = getField('hsCode');
  const shippingOrigin = getField('shippingOrigin');
  const fulfillmentService = getField('fulfillmentService');
  const inventoryPolicy = getField('inventoryPolicy');

  if (weight) {addTriple('weight', '=', `${weight}${weightUnit || 'g'}`);}
  if (barcode) {addTriple('barcode', '=', barcode);}
  if (hsCode) {addTriple('hs_code', '=', hsCode);}
  if (shippingOrigin) {addTriple('shipping_origin', '=', shippingOrigin);}
  if (fulfillmentService) {addTriple('fulfillment_service', '=', fulfillmentService);}
  if (inventoryPolicy) {addTriple('inventory_policy', '=', inventoryPolicy.toLowerCase());}

  // ========== Tax & Pricing ==========
  const chargeTax = getField('chargeTax');
  const taxCode = getField('taxCode');
  const costPerItem = getField('costPerItem');

  if (chargeTax !== null) {addTriple('taxable', '=', chargeTax.toLowerCase() === 'true' || chargeTax === 'Y' ? 'true' : 'false');}
  if (taxCode) {addTriple('tax_code', '=', taxCode);}
  if (costPerItem) {addTriple('cost', '=', costPerItem);}

  // ========== Product Type ==========
  const productCategory = getField('productCategory');
  const customProductType = getField('customProductType');

  if (productCategory) {addTriple('product_category', '=', productCategory);}
  if (customProductType) {addTriple('product_type', '=', customProductType);}

  return triples;
}
